import React from 'react';
import './CancelLeaveConfirmModal.css';

function CancelLeaveConfirmModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  leaveRequest,
  formatDate,
  formatDays,
  isEarlyReturn = false,
  daysRefunded = 0
}) {
  if (!isOpen || !leaveRequest) return null;

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const isApproved = leaveRequest.status === 'approved';

  return (
    <div className="cancel-leave-modal-overlay" onClick={handleCancel}>
      <div className="cancel-leave-modal" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="cancel-leave-modal-header">
          <div className="cancel-leave-header-content">
            <h3 className="cancel-leave-modal-title">
              {isEarlyReturn 
                ? 'Return from Leave Early?' 
                : (isApproved ? 'Cancel Approved Leave Request' : 'Cancel Leave Request')
              }
            </h3>
          </div>
          <button className="cancel-leave-close-btn" onClick={handleCancel}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="cancel-leave-modal-body">
          {isEarlyReturn ? (
            <div className="cancel-leave-warning-banner" style={{backgroundColor: '#e3f2fd', borderColor: '#2196F3'}}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2196F3" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
              <div className="cancel-leave-warning-text">
                <strong>Early Return Notice</strong>
                <p>You are currently on leave and returning early. Your leave balance will be credited for the unused days.</p>
              </div>
            </div>
          ) : isApproved && (
            <div className="cancel-leave-warning-banner">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
              <div className="cancel-leave-warning-text">
                <strong>Important Notice</strong>
                <p>You are about to cancel an approved leave request. This action cannot be undone.</p>
              </div>
            </div>
          )}

          <div className="cancel-leave-confirm-message">
            <p className="cancel-leave-question">
              {isEarlyReturn 
                ? 'Confirm that you are returning from leave early?' 
                : 'Are you sure you want to cancel this leave request?'
              }
            </p>
          </div>

          {/* Leave Details Card */}
          <div className="cancel-leave-details-card">
            <h4 className="cancel-leave-details-title">Leave Request Details</h4>
            
            <div className="cancel-leave-detail-row">
              <div className="cancel-leave-detail-label">Leave Type</div>
              <div className="cancel-leave-detail-value">
                <span 
                  className="cancel-leave-type-dot" 
                  style={{backgroundColor: leaveRequest.leave_type_details?.color || '#4CAF50'}}
                ></span>
                {leaveRequest.leave_type_details?.name || 'Unknown'}
              </div>
            </div>

            <div className="cancel-leave-detail-row">
              <div className="cancel-leave-detail-label">Start Date</div>
              <div className="cancel-leave-detail-value">
                {formatDate(leaveRequest.start_date)}
              </div>
            </div>

            <div className="cancel-leave-detail-row">
              <div className="cancel-leave-detail-label">End Date</div>
              <div className="cancel-leave-detail-value">
                {formatDate(leaveRequest.end_date)}
              </div>
            </div>

            <div className="cancel-leave-detail-row">
              <div className="cancel-leave-detail-label">Total Days</div>
              <div className="cancel-leave-detail-value cancel-leave-days-highlight">
                {formatDays(leaveRequest.total_days)} days
              </div>
            </div>

            <div className="cancel-leave-detail-row">
              <div className="cancel-leave-detail-label">Status</div>
              <div className="cancel-leave-detail-value">
                <span className={`cancel-leave-status-badge ${isApproved ? 'approved' : 'pending'}`}>
                  {leaveRequest.status.charAt(0).toUpperCase() + leaveRequest.status.slice(1)}
                </span>
              </div>
            </div>

            {leaveRequest.reason && (
              <div className="cancel-leave-detail-row cancel-leave-reason-row">
                <div className="cancel-leave-detail-label">Reason</div>
                <div className="cancel-leave-detail-value cancel-leave-reason-text">
                  {leaveRequest.reason}
                </div>
              </div>
            )}
          </div>

          {/* Impact Notice */}
          <div className="cancel-leave-impact-notice">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
            <div className="cancel-leave-impact-text">
              <strong>What happens next:</strong>
              {isEarlyReturn ? (
                <ul>
                  <li>Your leave will be marked as completed today</li>
                  <li>{formatDays(daysRefunded)} day{daysRefunded !== 1 ? 's' : ''} will be credited back to your balance</li>
                  <li>Your supervisor and HR will be notified of your early return</li>
                </ul>
              ) : (
                <ul>
                  <li>Your leave request will be marked as cancelled</li>
                  <li>Your leave balance will be restored ({formatDays(leaveRequest.total_days)} days)</li>
                  {isApproved && <li>HR will be notified of this cancellation</li>}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="cancel-leave-modal-footer">
          <button className="cancel-leave-btn-secondary" onClick={handleCancel}>
            {isEarlyReturn ? 'Go Back' : 'Keep Request'}
          </button>
          <button className="cancel-leave-btn-danger" onClick={handleConfirm}>
            {isEarlyReturn ? 'Yes, Return Early' : 'Yes, Cancel Request'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CancelLeaveConfirmModal;

